import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Star, StarFill, XLg } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { errorInfo, uploadManualProduct } from '../../api/onboarding';
import { updateManualProduct } from '../../api/dropGroups';
import { BRAND_IMAGE_ACCEPT, PRODUCT_CURRENCIES, PRODUCT_IMAGES_MAX } from '../../config/onboarding';
import { imageProblem, normalizePrice, parseSizes, productErrorField, validateProductForm, PRODUCT_DESCRIPTION_MAX } from '../onboarding/brandForm';

// "Termék feltöltése" / "Termék szerkesztése": a drop's product, by hand.
//   new:  POST /brands/:id/products/manual — 1–8 photos, the first is the main
//         one. Never published from here (a drop takes DRAFT products).
//   edit: PUT  /brands/:id/products/manual/:productId (§3b) — only what
//         changed; kept photos stay first, new ones are added after them,
//         removed ones go as `removeImages`. A change to the photos, the name
//         or the description re-runs the AI.
// Either way `onDone` gets the answer { product, labels, usable, reason, … }.

const SIZE_PRESETS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const SERVER_CODES = {
  INVALID_NAME: 'invalid',
  INVALID_PRICE: 'invalid',
  INVALID_CURRENCY: 'invalid',
  INVALID_SIZES: 'invalid',
  INVALID_DESCRIPTION: 'tooLong',
  IMAGES_REQUIRED: 'required',
  UNSUPPORTED_IMAGE_TYPE: 'type',
  IMAGE_TOO_LARGE: 'size',
  IMAGE_CORRUPT: 'corrupt',
  UNEXPECTED_FILE_FIELD: 'tooMany',
  TOO_MANY_FILES: 'tooMany',
  INVALID_REMOVE_IMAGES: 'corrupt',
};

const srcOf = (img) => (typeof img === 'string' ? img : img?.src);
const sizesOf = (p) => (p?.variants || []).map((v) => v.title || v.option1).filter(Boolean);
const descriptionOf = (p) => p?.original_description ?? p?.description ?? '';

export default function ManualProductForm({ brand, product = null, defaultCurrency = 'HUF', onDone, onCancel }) {
  const { t } = useLocale();
  const editing = !!product;
  const initial = useMemo(() => ({
    name: product?.name || '',
    price: product?.price_data?.price != null ? String(Number(product.price_data.price)) : '',
    currency: product?.price_data?.currency || defaultCurrency,
    sizes: sizesOf(product).join(', '),
    description: descriptionOf(product) || '',
  }), [product, defaultCurrency]);
  // [{ kind: 'kept', src } | { kind: 'new', file, url }]
  const [photos, setPhotos] = useState(() => (product?.images || []).map(srcOf).filter(Boolean).map((src) => ({ kind: 'kept', src, url: src })));
  const [name, setName] = useState(initial.name);
  const [price, setPrice] = useState(initial.price);
  const [currency, setCurrency] = useState(initial.currency);
  const [sizesText, setSizesText] = useState(initial.sizes);
  const [description, setDescription] = useState(initial.description);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const urls = useRef([]);

  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const sizes = useMemo(() => parseSizes(sizesText), [sizesText]);
  const removed = editing
    ? (product.images || []).map(srcOf).filter((src) => src && !photos.some((p) => p.kind === 'kept' && p.src === src))
    : [];

  function addPhotos(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = PRODUCT_IMAGES_MAX - photos.length;
    const bad = files.find((f) => imageProblem(f));
    const good = files.filter((f) => !imageProblem(f)).slice(0, Math.max(0, room));
    const next = good.map((file) => {
      const url = URL.createObjectURL(file);
      urls.current.push(url);
      return { kind: 'new', file, url };
    });
    setPhotos((p) => [...p, ...next]);
    if (bad) setErrors((x) => ({ ...x, images: { code: imageProblem(bad), name: bad.name } }));
    else if (files.length > room) setErrors((x) => ({ ...x, images: 'tooMany' }));
    else setErrors((x) => ({ ...x, images: undefined }));
  }

  function removePhoto(i) {
    setPhotos((p) => p.filter((_, j) => j !== i));
    setErrors((x) => ({ ...x, images: undefined }));
  }

  // New uploads only: the API keeps the stored photos first when editing.
  function makeMain(i) {
    setPhotos((p) => [p[i], ...p.filter((_, j) => j !== i)]);
  }

  function toggleSize(size) {
    const has = sizes.some((s) => s.toLowerCase() === size.toLowerCase());
    const next = has ? sizes.filter((s) => s.toLowerCase() !== size.toLowerCase()) : [...sizes, size];
    setSizesText(next.join(', '));
  }

  function clear(key) {
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  }

  function errorText(key) {
    const e = errors[key];
    if (!e) return null;
    const code = typeof e === 'object' ? e.code : e;
    const vars = { max: PRODUCT_IMAGES_MAX, n: typeof e === 'object' && e.index != null ? e.index + 1 : '', name: typeof e === 'object' ? e.name || '' : '' };
    const suffix = typeof e !== 'object' ? '' : e.index != null ? 'At' : e.name ? 'Named' : '';
    return (
      <p className="onb-error" role="alert" data-testid={`prod-error-${key}`}>
        {t(`drops.upload.error.${key}.${code}${suffix}`, vars)}
      </p>
    );
  }

  function changes(value) {
    const out = {};
    if (value.name !== initial.name.trim()) out.name = value.name;
    if (normalizePrice(initial.price) !== value.price) out.price = value.price;
    if (value.currency !== initial.currency.toUpperCase()) out.currency = value.currency;
    if (value.description !== initial.description.trim()) out.description = value.description;
    if (value.sizes.join('\n') !== parseSizes(initial.sizes).join('\n')) out.sizes = value.sizes;
    const added = photos.filter((p) => p.kind === 'new').map((p) => p.file);
    if (added.length) out.images = added;
    if (removed.length) out.removeImages = removed;
    return out;
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setFormError(null);
    const { errors: errs, value } = validateProductForm({
      images: photos.map((p) => (p.kind === 'new' ? p.file : p.src)), name, price, currency, sizes, description,
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    let patch = null;
    if (editing) {
      patch = changes(value);
      if (Object.keys(patch).length === 0) {
        onCancel?.();
        return;
      }
    }
    setBusy(true);
    try {
      const result = editing
        ? await updateManualProduct(brand.id, product, patch)
        : await uploadManualProduct(brand.id, { ...value, images: photos.map((p) => p.file) });
      onDone(result);
    } catch (err) {
      const info = errorInfo(err);
      const field = info.status === 400 ? productErrorField(info) : null;
      if (field) {
        const code = SERVER_CODES[info.code] || 'invalid';
        setErrors({ [field]: info.index != null ? { code, index: info.index } : code });
      } else if (info.status === 429) {
        setFormError(info.retryAfter
          ? t('drops.upload.error.rateLimitedFor', { min: Math.max(1, Math.ceil(info.retryAfter / 60)) })
          : t('drops.upload.error.rateLimited'));
      } else if (info.status === 403) {
        setFormError(t('drops.upload.error.forbidden'));
      } else if (info.status === 404 && editing) {
        setFormError(t('drops.upload.error.gone'));
      } else {
        setFormError(t('drops.upload.error.failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="drp-form" onSubmit={submit} noValidate data-testid={editing ? 'product-edit-form' : 'product-form'}>
      {formError && <div className="onb-banner" role="alert" data-testid="prod-form-error">{formError}</div>}

      <div className="drp-block">
        <div className="drp-label">
          {t('drops.upload.photos')}
          <span className="drp-label__count">{photos.length}/{PRODUCT_IMAGES_MAX}</span>
        </div>
        <div className={`drp-photos${errors.images ? ' is-invalid' : ''}`}>
          {photos.map((p, i) => (
            <div className="drp-photo" key={p.url} data-testid="prod-photo">
              <img src={p.url} alt="" />
              {i === 0 ? (
                <span className="drp-photo__main"><StarFill size={10} aria-hidden="true" />{t('drops.upload.main')}</span>
              ) : !editing ? (
                <button type="button" className="drp-photo__btn drp-photo__btn--star" onClick={() => makeMain(i)} title={t('drops.upload.makeMain')} aria-label={t('drops.upload.makeMain')}>
                  <Star size={12} />
                </button>
              ) : null}
              <button type="button" className="drp-photo__btn drp-photo__btn--x" onClick={() => removePhoto(i)} title={t('drops.upload.remove')} aria-label={t('drops.upload.remove')} data-testid="prod-photo-remove">
                <XLg size={11} />
              </button>
            </div>
          ))}
          {photos.length < PRODUCT_IMAGES_MAX && (
            <label className="drp-photo drp-photo--add">
              <Camera size={20} aria-hidden="true" />
              <span>{t(photos.length ? 'drops.upload.addMore' : 'drops.upload.add')}</span>
              <input type="file" accept={BRAND_IMAGE_ACCEPT} multiple onChange={addPhotos} hidden data-testid="prod-images" />
            </label>
          )}
        </div>
        {errorText('images') || (
          <p className="onb-hint">{t(editing ? 'drops.edit.photosHint' : 'drops.upload.photosHint', { max: PRODUCT_IMAGES_MAX })}</p>
        )}
      </div>

      <label className="field">
        <span>{t('drops.upload.name')}</span>
        <input value={name} onChange={(e) => { setName(e.target.value); clear('name'); }} aria-invalid={errors.name ? true : undefined} data-testid="prod-name" />
      </label>
      {errorText('name')}

      <div className="field-row">
        <label className="field">
          <span>{t('drops.upload.price')}</span>
          <input value={price} onChange={(e) => { setPrice(e.target.value); clear('price'); }} inputMode="decimal" placeholder="49990" aria-invalid={errors.price ? true : undefined} data-testid="prod-price" />
        </label>
        <label className="field drp-currency">
          <span>{t('drops.upload.currency')}</span>
          <select className="drp-select" value={currency} onChange={(e) => { setCurrency(e.target.value); clear('currency'); }} data-testid="prod-currency">
            {[...new Set([...PRODUCT_CURRENCIES, currency])].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      {errorText('price')}
      {errorText('currency')}

      <div className="drp-block">
        <label className="field drp-sizes">
          <span>{t('drops.upload.sizes')}</span>
          <input value={sizesText} onChange={(e) => { setSizesText(e.target.value); clear('sizes'); }} placeholder="S, M, L" aria-invalid={errors.sizes ? true : undefined} data-testid="prod-sizes" />
        </label>
        <div className="drp-chips" role="group" aria-label={t('drops.upload.sizes')}>
          {SIZE_PRESETS.map((s) => {
            const on = sizes.some((x) => x.toLowerCase() === s.toLowerCase());
            return (
              <button type="button" key={s} className={`drp-chip${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggleSize(s)}>{s}</button>
            );
          })}
          {sizes.filter((s) => !SIZE_PRESETS.some((p) => p.toLowerCase() === s.toLowerCase())).map((s) => (
            <span key={s} className="drp-chip is-on is-custom">{s}</span>
          ))}
        </div>
        {errorText('sizes') || <p className="onb-hint">{t('drops.upload.sizesHint')}</p>}
      </div>

      <label className="field">
        <span>{t('drops.upload.description')}</span>
        <textarea rows={4} value={description} maxLength={PRODUCT_DESCRIPTION_MAX} onChange={(e) => { setDescription(e.target.value); clear('description'); }} data-testid="prod-description" />
      </label>
      {errorText('description')}

      {editing && <p className="onb-hint">{t('drops.edit.reanalyzeHint')}</p>}

      <div className="drp-actions">
        {onCancel && <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={onCancel}>{t('billing.cancel')}</button>}
        <button type="submit" className="btn-o btn-o--dark" disabled={busy} data-testid="prod-submit">
          {busy ? t(editing ? 'profile.saving' : 'drops.upload.uploading') : t(editing ? 'drops.edit.saveProduct' : 'drops.upload.submit')}
        </button>
      </div>
      {busy && <p className="onb-hint drp-busy-hint">{t('drops.upload.uploadingHint')}</p>}
    </form>
  );
}
